import { useState } from "react";
import { ecsClient } from "@/services";
import { OutboundCallSession } from "@/types/outboundCall";
import { RefundProcess } from "@/types/refundProcess";

export type StartRefundHotelRequest = {
  confirmationNumber: string;
};

export type HotelRefundResponse = {
  success: boolean;
  message: string;
  session: OutboundCallSession;
  refundProcess: RefundProcess;
};

export const useHotelRefund = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateHotelRefund = async (
    params: StartRefundHotelRequest
  ): Promise<HotelRefundResponse | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await ecsClient.post(
        "/refund/hotel",
        {
          confirmationNumber: params.confirmationNumber,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        return response.data;
      } else {
        const errorMessage =
          response.data?.message || "Failed to initiate hotel refund";
        console.error("Hotel refund failed:", errorMessage);
        setError(errorMessage);
        return null;
      }
    } catch (err: any) {
      let errorMessage = "Unknown error occurred";
      
      if (err.response && err.response.data) {
        // API returned an error response
        errorMessage = err.response.data.message || err.response.data.error || `Request failed with status ${err.response.status}`;
      } else if (err.message) {
        // Network or other error
        errorMessage = err.message;
      }
      
      console.error("Error initiating hotel refund:", err);
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    isLoading,
    error,
    initiateHotelRefund,
    clearError,
  };
};
